import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Container, Row, Col } from 'react-bootstrap';
import {
  FaLanguage,
  FaMoneyBillWave,
  FaPassport,
  FaPlug,
  FaBuilding,
  FaCloudSun,
  FaMobileAlt,
  FaPrayingHands,
  FaChevronLeft,
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const QuickFactsContainer = styled(Container)`
  margin: 2rem auto;
  max-width: 800px;
  min-height: 400px;
`;

const Title = styled.h2`
  font-style: italic;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1.5rem;
  font-size: 1.8rem;
  font-weight: 600;
  text-align: right;
  border-bottom: 2px solid ${({ theme }) => theme.colors.surface.light};
  padding-bottom: 0.5rem;
`;

const FactItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  text-align: right;
  min-height: 50px;
`;

const IconWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.surface.light};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 1rem;
  color: ${({ theme }) => theme.colors.primary};
`;

const FactContent = styled.div`
  flex: 1;
`;

const FactTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FactValue = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0.2rem 0 0;
`;

const FactLink = styled(Link)`
  font-size: 0.9rem;
  position: relative;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0.2rem 0 0;
  text-decoration: none;
  display: inline-block;
  transition: all 0.3s ease;
  padding-bottom: 2px;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 1px;
    bottom: 0;
    right: 0;
    background-color: ${({ theme }) => theme.colors.primary};
    transition: width 0.3s ease;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 500;

    &::after {
      width: 100%;
    }
  }
`;

const LoadingPlaceholder = styled.div`
  width: 100px;
  height: 20px;
  background: ${({ theme }) => theme.colors.surface.light};
  border-radius: 4px;
  animation: pulse 1.5s infinite;

  @keyframes pulse {
    0% {
      opacity: 0.6;
    }
    50% {
      opacity: 0.3;
    }
    100% {
      opacity: 0.6;
    }
  }
`;

interface CountryFacts {
  language: string;
  currency: string;
  currencyCode: string;
  visa: string;
  powerPlugs: string;
  religion: string;
  capital: string;
  climate: string;
  apps: string;
}

interface Props {
  countryCode: string;
}

const countryData: Record<string, CountryFacts> = {
  thailand: {
    language: 'תאית',
    currency: 'באט תאילנדי',
    currencyCode: 'THB',
    visa: 'פטור מויזה ל-60 יום',
    powerPlugs: 'Type A / B / C',
    religion: 'בודהיזם',
    capital: 'בנגקוק',
    climate: 'טרופי; חם ולח כל השנה. עונת היובש היא נובמבר-אפריל',
    apps: 'Line, Grab, Bolt, InDrive, Foodpanda',
  },
  japan: {
    language: 'יפנית',
    currency: 'ין יפני',
    currencyCode: 'JPY',
    visa: 'פטור מויזה ל-90 יום',
    powerPlugs: 'Type A / B',
    religion: 'שינטו ובודהיזם',
    capital: 'טוקיו',
    climate: 'ארבע עונות מובהקות. קיץ חם ולח, חורף קר',
    apps: 'Line, Japan Travel',
  },
  vietnam: {
    language: 'וייטנאמית',
    currency: 'דונג וייטנאמי',
    currencyCode: 'VND',
    visa: 'ויזה נדרשת מראש',
    powerPlugs: 'Type A / C',
    religion: 'בודהיזם',
    capital: 'האנוי',
    climate: 'טרופי; חם ולח. עונת הגשמים מאי-ספטמבר',
    apps: 'Google Maps, Grab',
  },
  korea: {
    language: 'קוריאנית',
    currency: 'וון דרום קוריאני',
    currencyCode: 'KRW',
    visa: 'פטור מויזה ל-90 יום',
    powerPlugs: 'Type C / F',
    religion: 'נצרות, בודהיזם',
    capital: 'סיאול',
    climate: 'ארבע עונות מובהקות. קיץ חם ולח, חורף קר',
    apps: 'KakaoTalk, Naver Map',
  },
  india: {
    language: 'הינדי, אנגלית',
    currency: 'רופי הודי',
    currencyCode: 'INR',
    visa: 'ויזה נדרשת מראש',
    powerPlugs: 'Type C / D / M',
    religion: 'הינדואיזם',
    capital: 'ניו דלהי',
    climate: 'משתנה לפי אזור. חם ברוב השנה, עונת מוסון יוני-ספטמבר',
    apps: 'Ola, Zomato',
  },
  cambodia: {
    language: 'חמרית',
    currency: 'דולר אמריקאי',
    currencyCode: 'USD',
    visa: 'ויזה בהגעה',
    powerPlugs: 'Type A / B / C',
    religion: 'בודהיזם',
    capital: 'פנום פן',
    climate: 'טרופי; חם ולח. עונת הגשמים מאי-אוקטובר',
    apps: 'Google Maps, PassApp',
  },
  laos: {
    language: 'לאו',
    currency: 'קיפ לאי',
    currencyCode: 'LAK',
    visa: 'ויזה בהגעה',
    powerPlugs: 'Type A / B / C / F',
    religion: 'בודהיזם',
    capital: 'ויינטיאן',
    climate: 'טרופי; חם ולח. עונת הגשמים מאי-אוקטובר',
    apps: 'WhatsApp',
  },
  philippines: {
    language: 'פיליפינית, אנגלית',
    currency: 'פזו פיליפיני',
    currencyCode: 'PHP',
    visa: 'פטור מויזה ל-30 יום',
    powerPlugs: 'Type A / B / C',
    religion: 'נצרות',
    capital: 'מנילה',
    climate: 'טרופי; חם ולח. עונת הטייפונים יוני-ספטמבר',
    apps: 'Grab, Angkas',
  },
  indonesia: {
    language: 'אינדונזית',
    currency: 'רופיה אינדונזית',
    currencyCode: 'IDR',
    visa: 'פטור מויזה ל-30 יום',
    powerPlugs: 'Type C / F',
    religion: 'איסלאם',
    capital: "ג'קרטה",
    climate: 'טרופי; חם ולח כל השנה. עונת הגשמים אוקטובר-אפריל',
    apps: 'Gojek, Grab',
  },
};

const QuickFacts: React.FC<Props> = ({ countryCode }) => {
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchExchangeRate = async () => {
      setIsLoading(true);
      try {
        const facts = countryData[countryCode];
        if (!facts) {
          setIsLoading(false);
          return;
        }

        const currencyToFetch =
          facts.currencyCode === 'VND' || facts.currencyCode === 'LAK'
            ? 'USD'
            : facts.currencyCode;

        const response = await fetch(
          `https://api.freecurrencyapi.com/v1/latest?apikey=${process.env.REACT_APP_CURRENCY_API_KEY}&base_currency=ILS&currencies=${currencyToFetch}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch exchange rate');
        }

        const data = await response.json();
        let rate;

        if (facts.currencyCode === 'VND') {
          rate = (1 / data.data.USD) * 0.000041;
        } else if (facts.currencyCode === 'LAK') {
          rate = (1 / data.data.USD) * 0.000051;
        } else {
          rate = 1 / data.data[facts.currencyCode];
        }

        setExchangeRate(rate);
        setError(null);
      } catch (err) {
        console.error('Exchange rate error:', err);
        setError('שגיאה בטעינת שער החליפין');
      } finally {
        setIsLoading(false);
      }
    };

    fetchExchangeRate();
  }, [countryCode]);

  const facts = countryData[countryCode];
  if (!facts) return null;

  const formatRate = (rate: number): string => {
    if (rate >= 1) {
      return rate.toFixed(2);
    } else {
      return rate.toFixed(4);
    }
  };

  const renderCapitalInfo = () => {
    const linkMap = {
      thailand: '/international/thailand/destination/bangkok',
      vietnam: '/international/vietnam/destination/hanoi',
      cambodia: '/international/cambodia/destination/phnom-penh',
    };
    const link = linkMap[countryCode as keyof typeof linkMap];

    if (link) {
      return (
        <FactLink to={link}>
          {facts.capital} <FaChevronLeft className="info-icon" />
        </FactLink>
      );
    }

    return <FactValue>{facts.capital}</FactValue>;
  };

  // ויזה section will be a link only for Vietnam
  const renderVisaInfo = () => {
    const linkMap = {
      vietnam: '/international/vietnam/article/visa',
      cambodia: '/international/cambodia/article/cambodia-visa',
    };
    const link = linkMap[countryCode as keyof typeof linkMap];

    if (link) {
      return (
        <FactLink to={link}>
          {facts.visa} <FaChevronLeft className="info-icon" />
        </FactLink>
      );
    }
    return <FactValue>{facts.visa}</FactValue>;
  };

  return (
    <QuickFactsContainer>
      <Title>דברים קטנים שטוב לדעת</Title>
      <Row>
        <Col md={6}>
          <FactItem>
            <IconWrapper>
              <FaLanguage size={20} />
            </IconWrapper>
            <FactContent>
              <FactTitle>שפה</FactTitle>
              <FactValue>{facts.language}</FactValue>
            </FactContent>
          </FactItem>

          <FactItem>
            <IconWrapper>
              <FaMoneyBillWave size={20} />
            </IconWrapper>
            <FactContent>
              <FactTitle>מטבע</FactTitle>
              <FactValue>
                {facts.currency} ({facts.currencyCode})
                {isLoading ? (
                  <LoadingPlaceholder />
                ) : exchangeRate ? (
                  <div style={{ fontSize: '0.85em', color: '#666' }}>
                    1 {facts.currencyCode} = ₪{formatRate(exchangeRate)}
                  </div>
                ) : error ? (
                  <div style={{ fontSize: '0.85em', color: '#f44336' }}>
                    {error}
                  </div>
                ) : null}
              </FactValue>
            </FactContent>
          </FactItem>

          <FactItem>
            <IconWrapper>
              <FaPassport size={20} />
            </IconWrapper>
            <FactContent>
              <FactTitle>ויזה</FactTitle>
              {renderVisaInfo()}
            </FactContent>
          </FactItem>

          <FactItem>
            <IconWrapper>
              <FaPlug size={20} />
            </IconWrapper>
            <FactContent>
              <FactTitle>שקעי חשמל</FactTitle>
              <FactValue>{facts.powerPlugs}</FactValue>
            </FactContent>
          </FactItem>
        </Col>

        <Col md={6}>
          <FactItem>
            <IconWrapper>
              <FaPrayingHands size={20} />
            </IconWrapper>
            <FactContent>
              <FactTitle>דת</FactTitle>
              <FactValue>{facts.religion}</FactValue>
            </FactContent>
          </FactItem>

          <FactItem>
            <IconWrapper>
              <FaBuilding size={20} />
            </IconWrapper>
            <FactContent>
              <FactTitle>עיר בירה</FactTitle>
              {renderCapitalInfo()}
            </FactContent>
          </FactItem>

          <FactItem>
            <IconWrapper>
              <FaCloudSun size={20} />
            </IconWrapper>
            <FactContent>
              <FactTitle>אקלים</FactTitle>
              <FactValue>{facts.climate}</FactValue>
            </FactContent>
          </FactItem>

          <FactItem>
            <IconWrapper>
              <FaMobileAlt size={20} />
            </IconWrapper>
            <FactContent>
              <FactTitle>אפליקציות שימושיות</FactTitle>
              <FactValue>{facts.apps}</FactValue>
            </FactContent>
          </FactItem>
        </Col>
      </Row>
    </QuickFactsContainer>
  );
};

export default QuickFacts;
